import { Component, type CSSProperties, type ErrorInfo, type ReactNode } from 'react';

import * as Sentry from '@sentry/react';
import { isNotProductionEnvironment } from '@treneo/environments';
import { logger } from '@treneo/shared/logger';

type AppErrorBoundaryProps = {
  children: ReactNode;
};

type AppErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
};

const CONTAINER_STYLE: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: '#fff',
  padding: '24px',
  overflow: 'auto',
  fontFamily: 'Roboto, sans-serif',
  fontSize: '14px'
};

const BUTTON_STYLE: CSSProperties = {
  padding: '8px 16px',
  marginTop: '16px',
  fontSize: '14px',
  cursor: 'pointer',
  backgroundColor: '#1976d2',
  color: '#fff',
  border: 'none',
  borderRadius: '4px'
};

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (isNotProductionEnvironment()) {
      logger.error('AppErrorBoundary caught:', error, errorInfo);
    }

    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack ?? undefined
        }
      }
    });
  }

  handleReload = (): void => {
    globalThis.window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      return (
        <div style={CONTAINER_STYLE} role='alert'>
          <h2>Something went wrong</h2>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {this.state.error.message}
          </pre>
          {this.state.error.stack != null && (
            <details style={{ marginTop: '16px' }}>
              <summary>Stack trace</summary>
              <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>
                {this.state.error.stack}
              </pre>
            </details>
          )}
          <button
            type='button'
            style={BUTTON_STYLE}
            onClick={this.handleReload}
            aria-label='Refresh page'
          >
            Refresh
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
